<script setup lang="ts">
import { computed } from "vue";
import { marked, type Tokens, type RendererObject } from "marked";
import DOMPurify from "dompurify";

export interface TcMarkdownRenderedProps {
  content: string;
}

export type RendererConfig = Pick<RendererObject, "link">;

const props = defineProps<TcMarkdownRenderedProps>();

const customRenderer: RendererConfig = {
  link(token: Tokens.Link): string {
    const innerHTML = token.text || token.href;

    const title = token.title ? ` title="${token.title}"` : "";
    return `<a href="${token.href}"${title} target="_blank" rel="noopener noreferrer">${innerHTML}</a>`;
  },
};

marked.use({ renderer: customRenderer });

const renderedMarkdown = computed((): string => {
  const rawHTML = marked(props.content) as string;
  const cleanHTML: string = DOMPurify.sanitize(rawHTML, {
    ALLOWED_ATTR: ["href", "title", "target", "rel"],
  });

  return cleanHTML;
});
</script>

<template>
  <div v-html="renderedMarkdown" class="tc_markdown" />
</template>

<style lang="scss">
.tc_markdown {
  ul,
  ol {
    @include sp.margin(l, 4);
  }
  ul {
    list-style-type: square;
  }

  table {
    @include sp.margin(r, 1);
    @include text.general(low, s);
    @include colors.color(content, high);

    width: 100%;
    border-collapse: collapse;
  }

  th,
  td {
    @include border(1, solid);
    @include colors.border(content, mid-solid);
    @include sp.padding(false, 2);
    @include text.modification(left);
  }

  thead {
    @include colors.bg(content, low);
    @include text.general(high, xs);
  }

  tbody tr:hover {
    @include colors.bg(content, low);
  }
}
</style>
